import { useContext, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Loader2, Search, Shield, ShieldCheck, Trophy, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { grantAdminPrivilege, revokeAdminPrivilege } from '../../firebase/adminUtils';
import { assignTournamentAdmin, revokeTournamentAdmin } from '../../utils/tournamentPermissions';
import { setUserPermissions } from '../../firebase/userPermissionsUtils';
import {
  ALL_PERMISSION_KEYS,
  PERMISSION_GROUPS,
  PERMISSION_META,
} from '../../constants/permissions';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../ui/sheet';
import { Switch } from '../ui/switch';
import PageHeader from './shell/PageHeader';

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const roleBadges = (row) => {
  const out = [];
  if (row.isSuperAdmin) out.push({ label: 'Super', variant: 'live' });
  if ((row.tournamentIds || []).length > 0)
    out.push({ label: `Tournaments · ${row.tournamentIds.length}`, variant: 'outline' });
  if ((row.permissions || []).length > 0)
    out.push({
      label: `${row.permissions.length} perm${row.permissions.length === 1 ? '' : 's'}`,
      variant: 'secondary',
    });
  return out;
};

const UserManagement = () => {
  const { currentUser, isSuperAdmin } = useContext(AuthContext);
  const superAdmin = isSuperAdmin();

  const [users, setUsers] = useState([]);
  const [superAdmins, setSuperAdmins] = useState(new Set());
  const [tournaments, setTournaments] = useState([]);
  const [permissionsByUser, setPermissionsByUser] = useState({});
  const [tournamentIdsByUser, setTournamentIdsByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openUser, setOpenUser] = useState(null);

  // Seed user list + tournaments once
  useEffect(() => {
    if (!superAdmin) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [usersSnap, tournamentsSnap] = await Promise.all([
          getDocs(collection(db, 'userProfiles')),
          getDocs(query(collection(db, 'tournaments'), orderBy('startDate', 'desc'))),
        ]);
        setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTournaments(tournamentsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        toast.error('Could not load users.', { description: err.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [superAdmin]);

  // Live listeners for role collections so UI updates immediately after grant/revoke
  useEffect(() => {
    if (!superAdmin) return undefined;
    const unsubs = [
      onSnapshot(collection(db, 'adminUsers'), (snap) => {
        setSuperAdmins(new Set(snap.docs.map((d) => d.id)));
      }),
      onSnapshot(collection(db, 'tournamentAdmins'), (snap) => {
        const byUser = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          if (!data.userId || !data.tournamentId) return;
          if (!byUser[data.userId]) byUser[data.userId] = [];
          byUser[data.userId].push(data.tournamentId);
        });
        setTournamentIdsByUser(byUser);
      }),
      onSnapshot(collection(db, 'userPermissions'), (snap) => {
        const byUser = {};
        snap.docs.forEach((d) => {
          byUser[d.id] = d.data().permissions || [];
        });
        setPermissionsByUser(byUser);
      }),
    ];
    return () => unsubs.forEach((fn) => fn());
  }, [superAdmin]);

  const enrichedUsers = useMemo(
    () =>
      users.map((u) => ({
        ...u,
        isSuperAdmin: superAdmins.has(u.id),
        tournamentIds: tournamentIdsByUser[u.id] || [],
        permissions: permissionsByUser[u.id] || [],
      })),
    [users, superAdmins, tournamentIdsByUser, permissionsByUser]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return enrichedUsers;
    return enrichedUsers.filter((u) =>
      [u.username, u.displayName, u.email, u.id].some((v) => (v || '').toLowerCase().includes(term))
    );
  }, [enrichedUsers, search]);

  const tournamentName = (id) => tournaments.find((t) => t.id === id)?.name || id;
  const openRow = openUser ? enrichedUsers.find((u) => u.id === openUser) : null;

  if (!superAdmin) {
    return (
      <div className="py-20 text-center">
        <Shield className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="mt-4 font-display text-2xl font-semibold">Super admin only</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Only super admins can manage users and permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Access"
        title="Users & permissions"
        description="Three access levels: super admin (implicit all), tournament admin (scoped), and individual permissions. Pick the lightest level that fits."
      />

      <div className="relative mb-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by username, email, or UID"
          className="pl-9"
        />
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-16 text-center text-sm text-muted-foreground">
          <UserX className="mx-auto mb-3 h-8 w-8 opacity-50" />
          {search ? 'No users match your search.' : 'No users yet.'}
        </div>
      ) : (
        <ul className="divide-y divide-border/60 border-y border-border/60">
          {filtered.map((u) => {
            const badges = roleBadges(u);
            return (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => setOpenUser(u.id)}
                  className="flex w-full items-center gap-4 px-2 py-4 text-left transition-colors hover:bg-secondary/40"
                >
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    {u.profilePicUrl && <AvatarImage src={u.profilePicUrl} alt="" />}
                    <AvatarFallback>{initials(u.username || u.email)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-display text-base font-medium">
                        {u.username || u.displayName || 'Unnamed'}
                      </span>
                      {u.id === currentUser?.uid && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-heat">
                          you
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {u.email || u.id}
                    </div>
                  </div>
                  <div className="hidden flex-wrap items-center gap-1.5 sm:flex">
                    {badges.length === 0 ? (
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        no roles
                      </span>
                    ) : (
                      badges.map((b, i) => (
                        <Badge key={`${b.label}-${i}`} variant={b.variant}>
                          {b.label}
                        </Badge>
                      ))
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <UserSheet
        user={openRow}
        tournaments={tournaments}
        tournamentName={tournamentName}
        currentUserId={currentUser?.uid}
        onClose={() => setOpenUser(null)}
      />
    </div>
  );
};

const UserSheet = ({ user, tournaments, tournamentName, currentUserId, onClose }) => {
  const [working, setWorking] = useState(null); // e.g. 'super', 'content', permission key
  const [tournamentToAdd, setTournamentToAdd] = useState('');
  const [permissionsDraft, setPermissionsDraft] = useState(null);
  const [savingPerms, setSavingPerms] = useState(false);

  useEffect(() => {
    setPermissionsDraft(user ? new Set(user.permissions || []) : null);
    setTournamentToAdd('');
  }, [user?.id, user?.permissions]);

  if (!user) return null;
  const isSelf = user.id === currentUserId;

  const handleToggleSuper = async () => {
    setWorking('super');
    try {
      if (user.isSuperAdmin) {
        if (isSelf) {
          toast.error('You cannot revoke your own super admin.');
          return;
        }
        const ok = await revokeAdminPrivilege(user.id, currentUserId);
        if (ok) toast.success('Super admin removed.');
        else toast.error('Could not remove super admin.');
      } else {
        const ok = await grantAdminPrivilege(user.id, currentUserId);
        if (ok) toast.success('Granted super admin.');
        else toast.error('Could not grant super admin.');
      }
    } catch (err) {
      toast.error('Operation failed.', { description: err.message });
    } finally {
      setWorking(null);
    }
  };

  const handleAssignTournament = async () => {
    if (!tournamentToAdd) return;
    setWorking(`tournament-add`);
    try {
      const ok = await assignTournamentAdmin(user.id, tournamentToAdd, currentUserId);
      if (ok) {
        toast.success(`Assigned ${tournamentName(tournamentToAdd)}.`);
        setTournamentToAdd('');
      } else {
        toast.error('Assign failed.');
      }
    } catch (err) {
      toast.error('Assign failed.', { description: err.message });
    } finally {
      setWorking(null);
    }
  };

  const handleRevokeTournament = async (tid) => {
    setWorking(`tournament-${tid}`);
    try {
      const ok = await revokeTournamentAdmin(user.id, tid);
      if (ok) toast.success('Tournament access revoked.');
      else toast.error('Revoke failed.');
    } catch (err) {
      toast.error('Revoke failed.', { description: err.message });
    } finally {
      setWorking(null);
    }
  };

  const togglePermission = (key) => {
    setPermissionsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const savePermissions = async () => {
    if (!permissionsDraft) return;
    setSavingPerms(true);
    try {
      await setUserPermissions(user.id, Array.from(permissionsDraft), currentUserId);
      toast.success('Permissions saved.');
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSavingPerms(false);
    }
  };

  const currentSet = new Set(user.permissions || []);
  const draftSet = permissionsDraft || new Set();
  const dirty =
    draftSet.size !== currentSet.size ||
    [...draftSet].some((k) => !currentSet.has(k)) ||
    [...currentSet].some((k) => !draftSet.has(k));

  const assignedTournamentNames = (user.tournamentIds || []).map((id) => ({
    id,
    name: tournamentName(id),
  }));

  const availableTournaments = tournaments.filter(
    (t) => !(user.tournamentIds || []).includes(t.id)
  );

  const groupedPermissions = PERMISSION_GROUPS.map((group) => ({
    group,
    keys: ALL_PERMISSION_KEYS.filter((k) => PERMISSION_META[k].group === group),
  }));

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              {user.profilePicUrl && <AvatarImage src={user.profilePicUrl} alt="" />}
              <AvatarFallback>{initials(user.username || user.email)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate font-display text-lg">
                {user.username || user.displayName || 'Unnamed'}
              </div>
              <div className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {user.email || user.id}
              </div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-8 py-6">
          {/* ROLE PRESETS */}
          <section>
            <h3 className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              Roles
            </h3>
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4 rounded-md border border-border/60 bg-background/40 px-4 py-3">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-4 w-4 text-heat" />
                  <div>
                    <div className="text-sm font-medium">Super admin</div>
                    <div className="text-xs text-muted-foreground">
                      Full platform access — all roles and permissions implicit.
                    </div>
                  </div>
                </div>
                <Switch
                  checked={user.isSuperAdmin}
                  disabled={working === 'super' || (user.isSuperAdmin && isSelf)}
                  onCheckedChange={handleToggleSuper}
                />
              </div>

              <div className="rounded-md border border-border/60 bg-background/40 px-4 py-3">
                <div className="flex items-start gap-3">
                  <Trophy className="mt-0.5 h-4 w-4" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Tournament admin</div>
                    <div className="text-xs text-muted-foreground">
                      Manage one or more specific tournaments. Select below.
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  {assignedTournamentNames.length > 0 ? (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {assignedTournamentNames.map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/20 px-2 py-1 text-xs"
                        >
                          <span className="max-w-[14rem] truncate">{t.name}</span>
                          <button
                            type="button"
                            onClick={() => handleRevokeTournament(t.id)}
                            disabled={working === `tournament-${t.id}`}
                            className="opacity-60 transition-opacity hover:text-destructive hover:opacity-100"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mb-3 text-xs text-muted-foreground">
                      Not assigned to any tournaments.
                    </p>
                  )}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      value={tournamentToAdd}
                      onChange={(e) => setTournamentToAdd(e.target.value)}
                      className="min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">— Select tournament —</option>
                      {availableTournaments.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleAssignTournament}
                      disabled={!tournamentToAdd || working === 'tournament-add'}
                      className="w-full shrink-0 sm:w-auto"
                    >
                      Assign
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* PERMISSIONS */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                Permissions
              </h3>
              {dirty && (
                <Button size="sm" onClick={savePermissions} disabled={savingPerms}>
                  {savingPerms ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Save changes
                </Button>
              )}
            </div>

            {user.isSuperAdmin && (
              <p className="mb-4 text-xs text-muted-foreground">
                Super admins have every permission implicitly. These toggles are ignored.
              </p>
            )}

            <div className="space-y-6">
              {groupedPermissions.map(({ group, keys }) => (
                <div key={group}>
                  <div className="mb-2 text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
                    {group}
                  </div>
                  <div className="space-y-2">
                    {keys.map((key) => {
                      const meta = PERMISSION_META[key];
                      return (
                        <label
                          key={key}
                          className="flex cursor-pointer items-start gap-3 rounded-md border border-border/60 bg-background/40 px-3 py-2.5 transition-colors hover:bg-secondary/30"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 cursor-pointer"
                            checked={draftSet.has(key) || user.isSuperAdmin}
                            disabled={user.isSuperAdmin}
                            onChange={() => togglePermission(key)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium">{meta.label}</div>
                            <div className="text-xs text-muted-foreground">{meta.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default UserManagement;
